/**
 * @file `<site-drawer>` — overlay sibling `.site-drawer-overlay`; `html.drawer-is-open` toggles overlay.
 * Variant selects sync with PDP `variant-selects` or `variant-custom`.
 */

class SiteDrawer extends HTMLElement {
  constructor() {
    super();
    this._overlay = null;
    this._trigger = null;
    this._regionSelect = null;
    this._shopData = null;
    this._scrollLockObserver = null;

    this._onKeydown = (e) => {
      if (e.code === 'Escape' && this.classList.contains('is-open')) this.close();
    };
    this._onOverlayClick = (e) => {
      e.preventDefault();
      this.close();
    };
    this._onTriggerClick = (e) => {
      e.preventDefault();
      if (this.classList.contains('is-open')) this.close();
      else this.open();
    };
    this._onClick = (e) => this._handleClick(e);
    this._onChange = (e) => this._handleVariantChange(e);

    const prev = this.previousElementSibling;
    this._overlay = prev?.classList?.contains('site-drawer-overlay') ? prev : null;
    this._trigger = this.dataset.triggerId ? document.getElementById(this.dataset.triggerId) : null;
  }

  connectedCallback() {
    this._regionSelect = this.querySelector('[data-shop-region-select]');
    this._overlay?.addEventListener('click', this._onOverlayClick);
    this._trigger?.addEventListener('click', this._onTriggerClick);
    this._regionSelect?.addEventListener('change', () => this._updateShopAvailability(false));
    this.addEventListener('click', this._onClick);
    this.addEventListener('change', this._onChange);
    document.addEventListener('keydown', this._onKeydown);
    this._bindStoreModalScrollLock();
  }

  disconnectedCallback() {
    this._scrollLockObserver?.disconnect();
    this._overlay?.removeEventListener('click', this._onOverlayClick);
    this._trigger?.removeEventListener('click', this._onTriggerClick);
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('change', this._onChange);
    document.removeEventListener('keydown', this._onKeydown);
    document.documentElement.classList.remove('drawer-is-open');
  }

  open() {
    if (this.classList.contains('is-open')) return;

    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('site-drawer-open');
    this._trigger?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('drawer-is-open');
    this.classList.add('is-open');
    this._syncDrawerFromMain();
    this._updateShopAvailability();
  }

  close() {
    this.classList.remove('store-detail-modal-open', 'is-open');
    document.body.classList.remove('site-drawer-open');
    this._trigger?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('drawer-is-open');
    this.setAttribute('hidden', '');
    this.setAttribute('aria-hidden', 'true');
  }

  /** @returns {Array<{ id: number, options: string[], shops: string }>} */
  _loadShopData() {
    const src = document.querySelector(`[data-shop-availability-json="${this.id}"]`);
    if (!src?.textContent) return [];
    try {
      return JSON.parse(src.textContent);
    } catch {
      return [];
    }
  }

  /** @returns {{ id: number, options: string[], shops: string } | undefined} */
  _getSelectedVariant() {
    const data = (this._shopData ??= this._loadShopData());
    const values = [...this.querySelectorAll('select.check-shop-drawer__variant-select')].map((s) => s.value);

    if (values.length) {
      const match = data.find(
        (v) => v.options.length === values.length && v.options.every((o, i) => o === values[i]),
      );
      if (match) return match;
    }

    const id = document.querySelector('.product-form form[action*="/cart/add"] input[name="id"]')?.value;
    return (id && data.find((v) => String(v.id) === id)) || data[0];
  }

  /** @param {unknown} shops */
  _shopCodes(shops) {
    if (Array.isArray(shops)) {
      return shops.map((code) => String(code).trim()).filter(Boolean);
    }

    if (typeof shops === 'string') {
      return shops.split(',').map((code) => code.trim()).filter(Boolean);
    }

    return [];
  }

  /** @param {HTMLElement} item */
  _regionMeta(item) {
    const label = (item.dataset.regionLabel || '').trim();
    const handle = (item.dataset.region || '').trim() || this._regionHandle(label);

    return { label, handle };
  }

  /**
   * @param {HTMLElement[]} items
   * @param {Set<string>} availableCodes
   */
  _availableRegions(items, availableCodes) {
    const regions = new Map();

    items.forEach((item) => {
      if (!availableCodes.has(item.dataset.id || '')) return;

      const { label, handle } = this._regionMeta(item);
      if (!label || !handle) return;

      if (!regions.has(handle)) regions.set(handle, { label });
    });

    return regions;
  }

  /** @param {HTMLElement} item @param {boolean} isHidden */
  _setShopItemHidden(item, isHidden) {
    item.hidden = isHidden;
    item.classList.toggle('hidden', isHidden);
    item.setAttribute('aria-hidden', isHidden ? 'true' : 'false');
  }

  /** @param {string} value */
  _regionHandle(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /** Copies PDP variant UI into drawer selects. */
  _syncDrawerFromMain() {
    const variantSelects = document.querySelector('variant-selects');
    const variantCustom = document.querySelector('variant-custom');

    this.querySelectorAll('select.check-shop-drawer__variant-select').forEach((sel) => {
      const idx = Number(sel.dataset.optionIndex);
      if (Number.isNaN(idx)) return;

      if (variantSelects) {
        const main = variantSelects.querySelectorAll('select')[idx];
        if (main) sel.value = main.value;
        return;
      }

      const key = (sel.dataset.optionNameCompare || '').toLowerCase();
      if (!variantCustom || !key) return;

      for (const radio of variantCustom.querySelectorAll('input[type="radio"]')) {
        if ((radio.getAttribute('data-optionname') || '').toLowerCase() === key && radio.checked) {
          sel.value = radio.value;
          break;
        }
      }
    });

    this._syncStoreDetailSizes();
  }

  _syncStoreDetailSizes() {
    const value = this.querySelector('select.check-shop-drawer-size__select')?.value?.trim();
    if (!value) return;

    this.querySelectorAll('[data-shop-detail-size]').forEach((el) => {
      const label = el.dataset.sizeLabel?.trim();
      if (label) el.textContent = `${label}: ${value}`;
    });
  }

  /** @param {Event} e */
  _handleVariantChange(e) {
    const sel = e.target;
    if (!(sel instanceof HTMLSelectElement) || !sel.classList.contains('check-shop-drawer__variant-select')) return;

    const idx = Number(sel.dataset.optionIndex);
    if (Number.isNaN(idx)) return;

    const refresh = () =>
      requestAnimationFrame(() => {
        this._updateShopAvailability();
        this._syncStoreDetailSizes();
      });

    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      const main = variantSelects.querySelectorAll('select')[idx];
      if (main) {
        main.value = sel.value;
        main.dispatchEvent(new Event('change', { bubbles: true }));
      }
      refresh();
      return;
    }

    const key = (sel.dataset.optionNameCompare || '').toLowerCase();
    const variantCustom = document.querySelector('variant-custom');
    if (!variantCustom || !key) return;

    for (const radio of variantCustom.querySelectorAll('input[type="radio"]')) {
      if ((radio.getAttribute('data-optionname') || '').toLowerCase() === key && radio.value === sel.value) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        refresh();
        break;
      }
    }
  }

  /** @param {MouseEvent} e */
  _handleClick(e) {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;

    if (target.closest('[data-store-modal-back]')) {
      e.preventDefault();
      target.closest('modal-dialog')?.hide?.();
      return;
    }

    if (target.closest('[data-site-drawer-dismiss]')) {
      e.preventDefault();
      this.close();
      return;
    }

    const trigger = target.closest('[data-shop-modal-trigger]');
    if (!trigger) return;

    e.preventDefault();
    document.querySelector(trigger.dataset.shopModalTrigger || '')?.show?.(trigger);
  }

  _bindStoreModalScrollLock() {
    if (this._scrollLockObserver) return;

    const sync = () => {
      this.classList.toggle(
        'store-detail-modal-open',
        Boolean(this.querySelector('modal-dialog.dialog__storeData--drawer[open]')),
      );
    };

    this._scrollLockObserver = new MutationObserver(sync);
    this._scrollLockObserver.observe(this, { subtree: true, attributes: true, attributeFilter: ['open'] });
    sync();
  }

  /** @param {Map<string, { label: string }>} regions */
  _fillRegionSelect(regions) {
    if (!this._regionSelect) return;

    const prev = this._regionSelect.value;
    this._regionSelect.querySelectorAll('option[data-generated="true"]').forEach((o) => o.remove());

    const sortLabel = (a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

    [...regions.entries()]
      .sort(([, a], [, b]) => sortLabel(a, b))
      .forEach(([handle, { label }]) => {
        if (!handle || !label) return;
        const opt = new Option(label, handle);
        opt.dataset.generated = 'true';
        this._regionSelect.append(opt);
      });

    this._regionSelect.value = prev && regions.has(prev) ? prev : '';
  }

  /** @param {boolean} [refreshRegions] */
  _updateShopAvailability(refreshRegions = true) {
    const list = this.querySelector('[data-shop-list]');
    if (!list) return;

    const items = [...list.querySelectorAll('li[data-id]')];
    const available = new Set(this._shopCodes(this._getSelectedVariant()?.shops));
    const regionOptions = this._availableRegions(items, available);
    const grouped = new Map();
    const sortLabel = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' });
    const selected = this._regionSelect?.value || '';

    items.forEach((item, index) => {
      if (!item.dataset.originalIndex) item.dataset.originalIndex = String(index);

      const isAvailable = available.has(item.dataset.id || '');
      this._setShopItemHidden(item, true);
      item.classList.toggle('unavailable', !isAvailable);

      if (!isAvailable) return;

      const { label, handle } = this._regionMeta(item);
      if (!label || !handle) return;
      if (selected && handle !== selected) return;

      if (!grouped.has(handle)) grouped.set(handle, { label, items: [] });
      grouped.get(handle).items.push(item);
    });

    list.querySelectorAll('[data-shop-region-heading]').forEach((heading) => heading.remove());
    if (refreshRegions) this._fillRegionSelect(regionOptions);

    let visibleCount = 0;

    [...grouped.entries()]
      .sort(([, a], [, b]) => sortLabel(a.label, b.label))
      .forEach(([, region]) => {
        const heading = document.createElement('li');
        heading.className = 'check-shop-drawer__region-heading';
        heading.dataset.shopRegionHeading = 'true';
        heading.setAttribute('role', 'presentation');
        heading.textContent = region.label;
        list.append(heading);

        region.items
          .sort((a, b) => Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex))
          .forEach((item) => {
            this._setShopItemHidden(item, false);
            list.append(item);
            visibleCount += 1;
          });
      });

    list.hidden = visibleCount === 0;

    if(visibleCount === 0){
      this.querySelector('[data-regions]').hidden = true;
      this.querySelector('[data-stores]').hidden = true;
      this.querySelector('[data-unavailable]').hidden = false;
    }
    else{
      this.querySelector('[data-regions]').hidden = false;
      this.querySelector('[data-stores]').hidden = false;
      this.querySelector('[data-unavailable]').hidden = true;
    }
  }
}

if (!customElements.get('site-drawer')) {
  customElements.define('site-drawer', SiteDrawer);
}
